/**
 * Authentication and session management store using Zustand
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { supabaseClient, hasValidSupabaseConfig } from '../config/supabase'
import { User } from '../types'
import toast from 'react-hot-toast'
import { sendLoginOtpEmail } from '../utils/emailNotify'

// ─── Session nonce polling ────────────────────────────────────────────────────
// When a user logs in from another device/tab, a new nonce is written to the DB.
// The polling loop here detects that mismatch and forces a sign-out.
const NONCE_KEY = 'trailblaze_session_nonce'
const PENDING_OTP_KEY = 'tb_pending_otp'
let nonceInterval: ReturnType<typeof setInterval> | null = null

function startNoncePolling(logout: () => Promise<void>) {
  stopNoncePolling()
  nonceInterval = setInterval(async () => {
    const storedNonce = localStorage.getItem(NONCE_KEY)
    if (!storedNonce) return
    try {
      const { data: dbNonce, error } = await supabaseClient.rpc('get_session_nonce')
      if (error || !dbNonce) return
      if (dbNonce !== storedNonce) {
        // Another login displaced this session
        localStorage.removeItem(NONCE_KEY)
        toast('You have been signed in elsewhere. This session has ended.', { icon: '⚠️' })
        await logout()
      }
    } catch { /* network blip — try again next tick */ }
  }, 30_000)
}

function stopNoncePolling() {
  if (nonceInterval !== null) {
    clearInterval(nonceInterval)
    nonceInterval = null
  }
}

// Supabase auth-state subscription — kept at module level so it persists across re-renders
let authStateSubscription: { unsubscribe: () => void } | null = null

// ─── Helper: build User object from get_my_profile() result ──────────────────
function buildUser(data: any): User {
  return {
    id: data.id,
    email: data.email,
    role: data.role,
    created_at: data.created_at,
    updated_at: data.updated_at,
    profile: data.alumni_profiles || data.admin_profiles || data.employer_profiles,
  }
}

interface AuthState {
  user: User | null
  loading: boolean
  initialized: boolean
  pendingOtpEmail: string | null
  login: (email: string, password: string) => Promise<boolean | 'mfa_required'>
  logout: (silent?: boolean) => Promise<void>
  register: (email: string, password: string, metadata?: Record<string, any>) => Promise<boolean>
  initialize: () => Promise<void>
  updateProfile: (profileData: any) => Promise<boolean>
  refreshUser: () => Promise<void>
  verifyOtp: (code: string, trustDevice: boolean) => Promise<{ success: boolean; error?: string; attemptsLeft?: number }>
  resendOtp: () => Promise<void>
  cancelOtp: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        loading: false,
        initialized: false,
        pendingOtpEmail: null,

        initialize: async () => {
          try {
            set({ loading: true })

            if (!hasValidSupabaseConfig()) {
              toast.error('Supabase is not properly configured. Please check your environment variables.')
              set({ user: null, loading: false, initialized: true })
              return
            }

            // ── OTP gate: if a pending OTP session exists, hold the user there ──
            const rawPending = sessionStorage.getItem(PENDING_OTP_KEY)
            if (rawPending) {
              try {
                const { email, expiresAt } = JSON.parse(rawPending)
                if (new Date(expiresAt) > new Date()) {
                  // Still valid — keep user on OTP screen
                  set({ pendingOtpEmail: email, loading: false, initialized: true })
                  return
                }
              } catch { /* malformed JSON — treat as expired */ }
              // Expired or malformed — clear and sign out
              sessionStorage.removeItem(PENDING_OTP_KEY)
              await supabaseClient.auth.signOut()
              set({ user: null, pendingOtpEmail: null, loading: false, initialized: true })
              return
            }

            // Subscribe to Supabase auth state changes (fires across all tabs).
            if (!authStateSubscription) {
              const { data: { subscription } } = supabaseClient.auth.onAuthStateChange((event, session) => {
                if (event === 'SIGNED_OUT') {
                  stopNoncePolling()
                  localStorage.removeItem(NONCE_KEY)
                  sessionStorage.removeItem(PENDING_OTP_KEY)
                  set({ user: null, pendingOtpEmail: null, loading: false })
                } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
                  const currentUser = get().user
                  if (currentUser && session?.user && session.user.id !== currentUser.id) {
                    stopNoncePolling()
                    localStorage.removeItem(NONCE_KEY)
                    toast('Another account signed in on this browser. Please log in again.', { icon: '⚠️' })
                    set({ user: null, loading: false })
                  }
                }
              })
              authStateSubscription = subscription
            }

            const { data: { session }, error } = await supabaseClient.auth.getSession()

            if (error) {
              console.error('Error getting session:', error.message)
              set({ user: null, loading: false, initialized: true })
              return
            }

            if (session?.user) {
              const { data: userData, error: userError } = await supabaseClient.rpc('get_my_profile')

              const profileMissing =
                !userError &&
                userData &&
                !userData.alumni_profiles &&
                !userData.admin_profiles &&
                !userData.employer_profiles

              if (userError || !userData || profileMissing) {
                if (userError) console.error('Error fetching user data:', userError.message)

                try {
                  await supabaseClient.rpc('create_user_profile')
                } catch (createErr) {
                  console.error('Failed to create user profile during init:', createErr)
                }

                const { data: newUserData } = await supabaseClient.rpc('get_my_profile')

                if (newUserData) {
                  const meta = session.user.user_metadata
                  const epProfile = newUserData.employer_profiles
                  if (
                    newUserData.role === 'employer' &&
                    meta?.is_industry_partner === true &&
                    Array.isArray(meta?.partner_departments) &&
                    meta.partner_departments.length > 0 &&
                    epProfile &&
                    epProfile.approval_status === 'approved' &&
                    !epProfile.is_industry_partner
                  ) {
                    try {
                      await supabaseClient.rpc('register_employer_partnerships', {
                        partner_department_names: meta.partner_departments,
                      })
                    } catch (partnerErr) {
                      console.error('Partnership registration failed during init:', partnerErr)
                    }
                    const { data: refreshedData } = await supabaseClient.rpc('get_my_profile')
                    if (refreshedData) {
                      set({ user: buildUser(refreshedData), loading: false, initialized: true })
                      return
                    }
                  }

                  set({ user: buildUser(newUserData), loading: false, initialized: true })
                  return
                }

                // Fallback
                const fallbackUser: User = {
                  id: session.user.id,
                  email: session.user.email || '',
                  role: 'alumni',
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                }
                set({ user: fallbackUser, loading: false, initialized: true })
              } else if (userData) {
                const meta = session.user.user_metadata
                const epProfile = userData.employer_profiles
                if (
                  userData.role === 'employer' &&
                  meta?.is_industry_partner === true &&
                  Array.isArray(meta?.partner_departments) &&
                  meta.partner_departments.length > 0 &&
                  epProfile &&
                  epProfile.approval_status === 'approved' &&
                  !epProfile.is_industry_partner
                ) {
                  try {
                    await supabaseClient.rpc('register_employer_partnerships', {
                      partner_department_names: meta.partner_departments,
                    })
                  } catch (partnerErr) {
                    console.error('Partnership registration failed during init:', partnerErr)
                  }
                  const { data: refreshedData } = await supabaseClient.rpc('get_my_profile')
                  if (refreshedData) {
                    set({ user: buildUser(refreshedData), loading: false, initialized: true })
                    return
                  }
                }

                set({ user: buildUser(userData), loading: false, initialized: true })
              }
            } else {
              set({ user: null, loading: false, initialized: true })
            }
          } catch (error: any) {
            console.error('Initialize error:', error.message)
            toast.error('Failed to initialize authentication')
            set({ user: null, loading: false, initialized: true })
          }
        },

        // ── Internal helper: complete login after credentials or OTP verified ──
        // Extracted so both the trusted-device path and verifyOtp() can reuse it.

        login: async (email: string, password: string): Promise<boolean | 'mfa_required'> => {
          try {
            set({ loading: true })

            if (!hasValidSupabaseConfig()) {
              toast.error('Supabase is not properly configured')
              set({ loading: false })
              return false
            }

            const { data, error } = await supabaseClient.auth.signInWithPassword({ email, password })

            if (error) {
              toast.error(error.message)
              set({ loading: false })
              return false
            }

            if (!data.user) {
              set({ loading: false })
              return false
            }

            const userId = data.user.id

            // ── Demo exemptions: skip 2FA for these specific user IDs ──────────
            const OTP_EXEMPT_USERS = new Set([
              'dae7645e-37ec-45ce-96ba-6a6766c5ba2a',
              '67146e98-1b08-41ed-82e8-ae37707a69ca',
              '76a24d64-0576-46d9-b3b2-da78e8b348b1',
              '8ece5ae2-6660-44e4-be69-2705d65e4555',
              '19433a7f-a9bc-4604-8ae4-0986c64a7eef',
              '27b43812-3772-46c9-82fc-9687684c0c84',
              'c65583dd-e57e-4259-8593-fc590757c903',
              'cb9ad5e1-50bb-4b32-85d1-ab2475706ad3',
            ])
            if (OTP_EXEMPT_USERS.has(userId)) {
              await _finishLogin(get, set)
              return true
            }

            // ── Check for a trusted device token ──────────────────────────────
            const storedToken = localStorage.getItem(`tb_device_${userId}`)
            if (storedToken) {
              const { data: trusted } = await supabaseClient.rpc('verify_trusted_device', {
                p_token: storedToken,
              })
              if (trusted === true) {
                // Trusted device — complete login without OTP
                await _finishLogin(get, set)
                return true
              }
              // Invalid or expired token — remove it and fall through to OTP
              localStorage.removeItem(`tb_device_${userId}`)
            }

            // ── New device — generate OTP and send email ──────────────────────
            const { data: code, error: otpErr } = await supabaseClient.rpc('generate_login_otp')
            if (otpErr || !code) {
              console.error('OTP generation failed:', otpErr)
              await supabaseClient.auth.signOut()
              toast.error('Failed to send verification code. Please try again.')
              set({ loading: false })
              return false
            }

            const meta = data.user.user_metadata
            const name = [meta?.first_name, meta?.last_name].filter(Boolean).join(' ') || 'User'

            await sendLoginOtpEmail(email, name, code as string)

            sessionStorage.setItem(PENDING_OTP_KEY, JSON.stringify({
              email,
              userId,
              expiresAt: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
            }))

            set({ pendingOtpEmail: email, loading: false })
            return 'mfa_required'

          } catch (error: any) {
            console.error('Login error:', error.message)
            toast.error('Login failed')
            set({ loading: false })
            return false
          }
        },

        verifyOtp: async (
          code: string,
          trustDevice: boolean,
        ): Promise<{ success: boolean; error?: string; attemptsLeft?: number }> => {
          try {
            const { data, error } = await supabaseClient.rpc('verify_login_otp', { p_code: code })
            if (error) return { success: false, error: 'server_error' }

            const result = data as { success: boolean; error?: string; attempts_left?: number }
            if (!result.success) {
              return {
                success: false,
                error: result.error,
                attemptsLeft: result.attempts_left,
              }
            }

                    // OTP accepted — read userId before touching state
            const raw = sessionStorage.getItem(PENDING_OTP_KEY)
            const { userId } = (raw ? JSON.parse(raw) : { userId: undefined }) as { userId?: string }

            // Complete login first; only clear pending state on success
            await _finishLogin(get, set)
            sessionStorage.removeItem(PENDING_OTP_KEY)
            set({ pendingOtpEmail: null })

            // Register trusted device (non-critical, fire-and-forget).
            // PostgrestBuilder implements PromiseLike but not full Promise,
            // so use .then(noop, noop) instead of .catch().
            if (trustDevice && userId) {
              const deviceToken = crypto.randomUUID()
              localStorage.setItem(`tb_device_${userId}`, deviceToken)
              supabaseClient.rpc('register_trusted_device', { p_token: deviceToken })
                .then(() => {}, () => {})
            }

            return { success: true }

          } catch (err: any) {
            console.error('verifyOtp error:', err)
            return { success: false, error: 'server_error' }
          }
        },

        resendOtp: async (): Promise<void> => {
          const email = get().pendingOtpEmail
          if (!email) return

          const { data: code, error } = await supabaseClient.rpc('generate_login_otp')
          if (error || !code) return

          const { data: { user } } = await supabaseClient.auth.getUser()
          const meta = user?.user_metadata
          const name = [meta?.first_name, meta?.last_name].filter(Boolean).join(' ') || 'User'

          await sendLoginOtpEmail(email, name, code as string)

          // Refresh the expiry in sessionStorage
          const raw = sessionStorage.getItem(PENDING_OTP_KEY)
          if (raw) {
            try {
              const parsed = JSON.parse(raw)
              sessionStorage.setItem(PENDING_OTP_KEY, JSON.stringify({
                ...parsed,
                expiresAt: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
              }))
            } catch { /* ignore */ }
          }
        },

        cancelOtp: async (): Promise<void> => {
          sessionStorage.removeItem(PENDING_OTP_KEY)
          await supabaseClient.auth.signOut()
          set({ pendingOtpEmail: null, user: null })
        },

        register: async (email: string, password: string, metadata: Record<string, any> = {}): Promise<boolean> => {
          try {
            set({ loading: true })

            if (!hasValidSupabaseConfig()) {
              toast.error('Supabase is not properly configured')
              set({ loading: false })
              return false
            }

            const role = metadata.role || 'alumni'

            const { data, error } = await supabaseClient.auth.signUp({
              email,
              password,
              options: { data: { ...metadata, role } },
            })

            if (error) {
              if (error.message.toLowerCase().includes('already registered') ||
                  error.message.toLowerCase().includes('already exists') ||
                  error.message.toLowerCase().includes('duplicate')) {
                set({ loading: false })
                throw new Error('EMAIL_ALREADY_REGISTERED_CONFIRMED')
              } else {
                toast.error(error.message)
              }
              set({ loading: false })
              return false
            }

            if (data.user) {
              if (!data.user.identities || data.user.identities.length === 0) {
                set({ loading: false })
                throw new Error('EMAIL_ALREADY_REGISTERED_CONFIRMED')
              }

              const accountAgeMs = Date.now() - new Date(data.user.created_at).getTime()
              if (accountAgeMs > 10000) {
                set({ loading: false })
                throw new Error('EMAIL_ALREADY_REGISTERED_UNCONFIRMED')
              }

              if (!data.session) {
                set({ loading: false })
                return true
              }

              try {
                const { data: profileData, error: profileError } = await supabaseClient.rpc('create_user_profile')

                if (profileError) {
                  console.error('Profile creation error:', profileError)
                  toast.error(`Profile creation failed: ${profileError.message}`)
                  set({ loading: false })
                  return false
                }

                console.log('Profile created successfully:', profileData)

                if (role === 'employer' && metadata.is_industry_partner === true &&
                    Array.isArray(metadata.partner_departments) && metadata.partner_departments.length > 0) {
                  const { error: partnerErr } = await supabaseClient.rpc('register_employer_partnerships', {
                    partner_department_names: metadata.partner_departments,
                  })
                  if (partnerErr) console.error('Partnership registration failed:', partnerErr)
                }

                if (role === 'alumni' && (metadata.first_name || metadata.last_name || metadata.graduation_year ||
                    metadata.department || metadata.program || metadata.current_employment_status)) {
                  const toDbValue = (v: string): string | null => {
                    if (!v) return null
                    const map: Record<string, string> = {
                      'Employed': 'employed', 'Unemployed': 'unemployed',
                      'Self-Employed': 'self-employed', 'Studying': 'studying',
                    }
                    return map[v] || v.toLowerCase().replace(/ /g, '_')
                  }
                  await supabaseClient.from('alumni_profiles').update({
                    first_name: metadata.first_name,
                    last_name: metadata.last_name,
                    year_graduated: metadata.graduation_year ? parseInt(metadata.graduation_year) : undefined,
                    department: metadata.department,
                    program: metadata.program,
                    current_employment_status: toDbValue(metadata.current_employment_status),
                    updated_at: new Date().toISOString(),
                  }).eq('user_id', data.user.id)
                }

                toast.success('Account created successfully! You can now sign in.')
                set({ loading: false })
                return true
              } catch (profileErr) {
                console.error('Profile creation failed:', profileErr)
                toast.error('Profile creation failed. Please contact support.')
                set({ loading: false })
                return false
              }
            }

            set({ loading: false })
            return false
          } catch (error: any) {
            if (error.message === 'EMAIL_ALREADY_REGISTERED_CONFIRMED' ||
                error.message === 'EMAIL_ALREADY_REGISTERED_UNCONFIRMED') {
              set({ loading: false })
              throw error
            }
            console.error('Registration error:', error.message)
            toast.error('Registration failed')
            set({ loading: false })
            return false
          }
        },

        logout: async (silent: boolean = false): Promise<void> => {
          try {
            set({ loading: true })
            stopNoncePolling()
            localStorage.removeItem(NONCE_KEY)
            sessionStorage.removeItem(PENDING_OTP_KEY)

            const { error } = await supabaseClient.auth.signOut()

            if (error) {
              console.error('Logout error:', error.message)
              if (!silent) toast.error('Logout failed')
            } else {
              if (!silent) toast.success('Successfully logged out')
            }

            set({ user: null, pendingOtpEmail: null, loading: false })
          } catch (error: any) {
            console.error('Logout error:', error.message)
            set({ user: null, pendingOtpEmail: null, loading: false })
          }
        },

        updateProfile: async (profileData: any): Promise<boolean> => {
          try {
            const { user } = get()
            if (!user) return false

            set({ loading: true })

            let tableName = 'alumni_profiles'
            if (user.role === 'admin') tableName = 'admin_profiles'
            if (user.role === 'employer') tableName = 'employer_profiles'

            const { error } = await supabaseClient.from(tableName).upsert({
              user_id: user.id,
              ...profileData,
              updated_at: new Date().toISOString(),
            })

            if (error) {
              toast.error('Failed to update profile')
              set({ loading: false })
              return false
            }

            await get().initialize()
            toast.success('Profile updated successfully!')
            return true
          } catch (error: any) {
            console.error('Update profile error:', error.message)
            toast.error('Failed to update profile')
            set({ loading: false })
            return false
          }
        },

        refreshUser: async () => {
          try {
            const { user } = get()
            if (!user) return

            const { data: userData, error: userError } = await supabaseClient.rpc('get_my_profile')
            if (!userError && userData) {
              set({ user: buildUser(userData) })
            }
          } catch (error: any) {
            console.error('Refresh user error:', error.message)
          }
        },
      }),
      {
        name: 'auth-storage',
        partialize: (state) => ({ user: state.user }),
      }
    ),
    { name: 'auth-store' }
  )
)

// ─── Internal: finish the login flow after credentials/OTP are verified ───────
// Fetches the user profile, sets it in the store, and starts the session nonce.
async function _finishLogin(
  get: () => AuthState,
  set: (partial: Partial<AuthState>) => void,
): Promise<void> {
  const { data: userData, error: userError } = await supabaseClient.rpc('get_my_profile')

  const profileMissing =
    !userError &&
    userData &&
    !userData.alumni_profiles &&
    !userData.admin_profiles &&
    !userData.employer_profiles

  if (userError || !userData || profileMissing) {
    if (userError) console.error('Error fetching user profile:', userError.message)
    try {
      await supabaseClient.rpc('create_user_profile')
    } catch (createErr) {
      console.error('Failed to create user profile:', createErr)
    }
    const { data: newUserData } = await supabaseClient.rpc('get_my_profile')
    if (newUserData) {
      set({ user: buildUser(newUserData), loading: false })
    } else {
      // Fallback
      const { data: { user: supaUser } } = await supabaseClient.auth.getUser()
      set({
        user: {
          id: supaUser!.id,
          email: supaUser!.email || '',
          role: 'alumni',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        loading: false,
      })
    }
  } else {
    set({ user: buildUser(userData), loading: false })
  }

  // Set session nonce
  try {
    const { data: nonce } = await supabaseClient.rpc('set_session_nonce')
    if (nonce) {
      localStorage.setItem(NONCE_KEY, nonce)
      startNoncePolling(get().logout)
    }
  } catch { /* non-critical */ }
}
